import { Store } from '@ngrx/store';
import * as angular from 'angular';
import { IController, ITimeoutService } from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import { ImSchool } from 'Src/ng2/shared/services/im-models/im-school';
import { UtilitiesService } from 'src/ng2/shared/services/utilities/utilities.service';
import { CurrentSchoolYear } from '../../../ng2/shared/constants/current-school-year.constant';
import { ChangeSavedInMetrics } from '../../../ng2/store/actions/metrics-actions';
import { SdcActionsService } from './../../sdc/flux/sdc-actions.service';
import { ISdcStore } from './../../sdc/flux/sdc-store.interface';
import { SpinnerModal } from './../../shared/modals/status-spinner/status-spinner.service';

export class RegentsMetricsCtrl implements IController {
  static $inject = [
    '$mdDialog',
    '$scope',
    '$timeout',
    'BackgroundJobSpinner',
    'ErrorModal',
    'ImSchool',
    'Rollbar',
    'SDCStore',
    'SpinnerModal',
    'Store',
    'UtilitiesService',
    'backgroundJob',
    'sdcActions',
  ];

  // Bindings
  private changeMadeInMetrics;
  private cohortsByTerm;
  private cohortsFollowSameSequence;
  private confirm;
  private currentSchool: any;
  private lastSaveTimeStamp;
  private nextFourGradYears;
  private regentsTableCopy;
  private term;
  private upcomingGradDates;

  constructor(
    private $mdDialog,
    private $scope,
    private $timeout: ITimeoutService,
    private BackgroundJobSpinner,
    private ErrorModal,
    private ImSchool: ImSchool,
    private Rollbar,
    private SDCStore: ISdcStore,
    private SpinnerModal: SpinnerModal,
    private store: Store<any>,
    private UtilitiesService: UtilitiesService,
    private backgroundJob,
    private sdcActions: SdcActionsService,
  ) {}

  $onInit() {
    this.SpinnerModal.deactivateSpinner();

    this.lastSaveTimeStamp = null;
    this.changeMadeInMetrics = { status: false };

    const regentsTermData = this.currentSchool.onTrackMetrics.regentsExpectedByAdmin;

    this.cohortsByTerm = this.trimSuperSeniorYrFromRegentsData(regentsTermData, CurrentSchoolYear.STARTFULL);
    this.cohortsFollowSameSequence = this.cohortsByTerm.allCohorts !== null ? true : false;
    this.term = this.currentSchool.termStructure;

    // COPY COHORT
    this.regentsTableCopy = angular.copy(this.cohortsByTerm);

    this.upcomingGradDates = this.ImSchool.getUpcomingGradDates();
    this.nextFourGradYears = _.uniq(_.map(this.upcomingGradDates, ({ year }) => `cohort_${year}`));

    // sdcAction to store:
    const updateSchool = () => {
      const storeSchool = this.SDCStore.getSchool();
      const storeSchoolCopy = this.UtilitiesService.copyPOJO(storeSchool);
      this.currentSchool = _.merge(this.currentSchool, storeSchoolCopy);
    };
    this.$scope.$listenTo(this.SDCStore, ['school'], updateSchool);
  }

  dialogConfirmation(ev) {
    // when changes cohort sequence:
    this.confirm = this.$mdDialog.confirm({
      title: 'Modify tables?',
      textContent: 'All unsaved changes will be lost.',
      ariaLabel: 'Warning',
      targetEvent: ev,
      ok: 'Continue',
      cancel: 'Cancel',
    });

    this.$mdDialog
      .show(this.confirm)
      .then(() => {
        if (this.cohortsFollowSameSequence) {
          this._bundleCohorts();
        } else {
          this._unbundleCohorts();
        }
      })
      .catch(err => {
        this.cohortsFollowSameSequence = !this.cohortsFollowSameSequence;
      });
  }

  trimSuperSeniorYrFromRegentsData(regentsTermData, endYr: string) {
    const superSeniorCohort = `cohort_${endYr}`;
    return _.omit(regentsTermData, superSeniorCohort);
  }

  save() {
    const path = 'onTrackMetrics.regentsExpectedByAdmin';
    const patch = this.currentSchool.generatePatch([path]);

    this.currentSchool
      .$patchFields([path], (cs, headers) => {
        const job = this.backgroundJob(headers('nv-background-jobs'));
        const backgroundJobPromise = job.promise();
        this.BackgroundJobSpinner.activateSpinner('Updating Students', backgroundJobPromise, job);

        backgroundJobPromise.then(
          () => {
            this.lastSaveTimeStamp = 'Last saved: ' + moment().format('MM/DD/YY \\at hh:mmA');
            this.changeMadeInMetrics.status = false;
            this.store.dispatch(new ChangeSavedInMetrics());
            this.regentsTableCopy = angular.copy(this.cohortsByTerm);
            this.sdcActions.updateSchoolByPatch(this.UtilitiesService.copyPOJO(patch));
          },
          error => {
            this.ErrorModal.show('An error occurred while updating students', error);
          },
          notification => {
            // tslint:disable-next-line:no-console
            if (notification) console.log(notification);
          },
        );
      })
      .catch(errorResponse => {
        this.ErrorModal.show('Unable to save your changes', errorResponse);
      });
  }

  validate() {
    return !this.changeMadeInMetrics.status;
  }

  _unbundleCohorts() {
    const allCohorts = this.cohortsByTerm.allCohorts;
    const cohortKeys = _.keys(this.cohortsByTerm);
    const newCohortsByTerm: any = {};
    _.pull(cohortKeys, 'allCohorts');
    // want to limit the keys here to prevent overwriting cohort history
    _.each(cohortKeys, key => {
      if (_.includes(this.nextFourGradYears, key)) newCohortsByTerm[key] = angular.copy(allCohorts);
    });
    newCohortsByTerm.allCohorts = null;
    this.currentSchool.onTrackMetrics.regentsExpectedByAdmin = newCohortsByTerm;
    this.cohortsByTerm = newCohortsByTerm;
  }

  _bundleCohorts() {
    const cohortKeys = _.keys(this.cohortsByTerm);
    // remove allCohorts:
    _.pull(cohortKeys, 'allCohorts');
    // sort:
    cohortKeys.sort((a, b) => parseInt(a) - parseInt(b));
    // update allCohorts prop with next grad cohort data:
    let newCohortsByTerm = { allCohorts: this.UtilitiesService.copyPOJO(this.cohortsByTerm[cohortKeys[0]]) };
    // want to limit the keys here to prevent overwriting cohort history
    _.each(cohortKeys, key => {
      if (_.includes(this.nextFourGradYears, key)) newCohortsByTerm[key] = null;
    });

    this.currentSchool.onTrackMetrics.regentsExpectedByAdmin = newCohortsByTerm;
    this.cohortsByTerm = newCohortsByTerm;
  }
}

angular.module('nv.SchoolMetricsModule').controller('RegentsMetricsCtrl', RegentsMetricsCtrl);
